import React, { Fragment, useState } from 'react';

import Byline from '@components/Byline';
import ArticleHeader from '@components/FeedItems/ArticleHeader';
import ArticleImage from '@components/FeedItems/ArticleImage';
import Highlight from '@components/FeedItems/Highlight';
import TopArticlesSection, { Page } from '@components/FeedItems/TopArticlesSection';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, TrackingLocation, click } from '@tracking';

import { Article, Briefing, FeedUnit, FeedUnits, UnitName } from '../../interfaces/homepage';
import { SecondaryButton } from '../Button/variants';
import ArticleFeedItem from '../FeedItems/ArticleFeedItem';
import BriefingFeedItem from '../FeedItems/BriefingFeedItem';
import Tabs from '../Tabs';
import ForumUnit from '../pages/Home/ForumUnit';
import NewsletterUnit from '../pages/Home/NewsletterUnit';

const PAGE_SIZE = 25;
const RECENT = 'recent';
const POPULAR = 'popular';
const ARTICLE = 'Article';

interface MainFeedProps {
  featuredArticles?: Article[];
  feedContent: Article[] | (Article | Briefing)[];
  feedUnits?: FeedUnits;
  includeTabs?: boolean;
  pageSize?: number;
  view: string;
}

const MainFeed = ({
  featuredArticles,
  feedContent,
  feedUnits,
  includeTabs = true,
  pageSize = PAGE_SIZE,
  view,
}: MainFeedProps) => {
  const [numVisibleRecords, setNumVisibleRecords] = useState(pageSize);
  const { Track, trackEvent } = useEventTracking({ location: TrackingLocation.MainRiver });

  const handleReadMore = () => {
    setNumVisibleRecords((prevNumVisibleRecords) => prevNumVisibleRecords + pageSize);
    trackEvent(click({ clickTarget: ClickTarget.ReadMore }));
  };

  const renderFeedUnit = (feedUnit: FeedUnit) => {
    if (feedUnit.unitName === UnitName.Forum) {
      return <ForumUnit feedUnit={feedUnit} />;
    } else {
      const isLatestArticle = feedUnit.unitName === UnitName.LatestNewsletters;
      const isTheWeekend = feedUnit.unitName === UnitName.TheWeekend;
      return (
        <NewsletterUnit
          feedUnit={feedUnit}
          isTheWeekend={isTheWeekend}
          isLatestArticle={isLatestArticle}
        />
      );
    }
  };

  return (
    <Track>
      <section>
        {includeTabs && (
          <Tabs
            tabs={[
              {
                to: RECENT,
                text: 'Latest',
              },
              {
                to: POPULAR,
                text: 'Most popular',
              },
            ]}
            view={view}
          />
        )}
        {featuredArticles?.length > 0 && (
          <TopArticlesSection
            page={Page.Home}
            sideArticles={featuredArticles.slice(1)}
            topArticle={featuredArticles[0]}
          />
        )}
        {feedContent.slice(0, numVisibleRecords).map((item, index) => (
          <Fragment key={`${item.contentType}-${item.id}`}>
            {feedUnits?.[index] && renderFeedUnit(feedUnits[index])}
            {item.contentType === ARTICLE ? (
              <ArticleFeedItem
                contentIndex={index}
                articleAuthors={item.authors}
                articleId={item.id}
                className="flex-col border-0 border-b border-solid border-grayscale-gray px-16 py-32 sm:flex-row sm:px-0 sm:py-48"
              >
                <ArticleImage
                  className="sm:h-[calc(302px*9/16)] sm:w-[302px] lg:h-[calc(416px*9/16)] lg:w-[416px]"
                  id={item.id}
                  isPro={item.isPro}
                  isMobileFullWidth
                  picture={item.picture}
                  sizes="(min-width: 1200px) 500px, (min-width: 768px) 300px, (min-width: 480px) 600px, 300px"
                  slug={item.slug}
                  title={item.title}
                />
                <div className="ml-0 mt-16 sm:ml-24 sm:mt-0">
                  {item.highlight && <Highlight highlight={item.highlight} />}
                  <ArticleHeader
                    className="text-headline-xs lg:text-headline-sm"
                    data-testid="article-title"
                    title={item.title}
                    slug={item.slug}
                  />
                  <Byline authors={item.authors} publishedAt={item.publishedAt} />
                </div>
              </ArticleFeedItem>
            ) : (
              <BriefingFeedItem briefing={item} />
            )}
          </Fragment>
        ))}
        {feedContent.length > numVisibleRecords && (
          <div className="px-16 pt-32 sm:px-0 sm:pt-48">
            <SecondaryButton
              className="bg-transparent focus:bg-transparent focus-visible:ring-0 max-sm:hover:!bg-transparent"
              as="button"
              onClickCapture={handleReadMore}
              fullWidth
            >
              Read more
            </SecondaryButton>
          </div>
        )}
      </section>
    </Track>
  );
};

export default MainFeed;
